import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store.ts";
import { sessionDataType } from "../../utils/types/app.types.ts";
import {
  applyBrowseDisplaySort,
  BROWSE_SORT_OPTIONS,
  BrowseResultSortMode,
  filterByMinVotes,
  MIN_VOTES_OPTIONS,
  parseMinVotesParam,
} from "../../utils/browseVoteUtils.ts";

type SearchHit = {
  id: number;
  poster_path: string | null;
  title?: string;
  name?: string;
  release_date?: string;
  first_air_date?: string;
  vote_average?: number;
  vote_count?: number;
};

const fetchSearch = async (
  endpoint: string,
  sessionData: sessionDataType,
): Promise<{
  results?: SearchHit[];
  total_results?: number;
  total_pages?: number;
}> => {
  const {
    api: { apiKey: API_KEY, apiUrl: API_URL },
  } = sessionData;

  if (window.showSpinner) {
    window.showSpinner();
  }

  const response = await fetch(
    `${API_URL}${endpoint}&api_key=${API_KEY}&language=en-US`,
  );
  const data = await response.json();

  window.hideSpinner();

  return data;
};

const SearchPage: React.FC = () => {
  const sessionData = useSelector((state: RootState) => state.session);
  const [searchParams] = useSearchParams();
  const searchTerm = searchParams.get("search-term") || "";
  const searchType = searchParams.get("type") || "movie";

  const [results, setResults] = useState<SearchHit[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortMode, setSortMode] = useState<BrowseResultSortMode>("relevance");
  const [minVotes, setMinVotes] = useState("");

  useEffect(() => {
    setPage(1);
  }, [searchTerm, searchType]);

  useEffect(() => {
    setSortMode("relevance");
    setMinVotes("");
  }, [searchTerm, searchType]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setResults([]);
      setTotalResults(0);
      setTotalPages(1);
      return;
    }

    const search = async () => {
      const endpoint =
        searchType === "tv"
          ? `search/tv?query=${encodeURIComponent(searchTerm)}&page=${page}`
          : `search/movie?query=${encodeURIComponent(searchTerm)}&page=${page}`;

      const data = await fetchSearch(endpoint, sessionData);
      setResults(data.results || []);
      setTotalResults(data.total_results || 0);
      setTotalPages(data.total_pages || 1);
    };

    void search();
  }, [searchTerm, searchType, page, sessionData]);

  const minVotesNum = parseMinVotesParam(minVotes);
  const displayResults = useMemo(() => {
    const filtered = filterByMinVotes(results, minVotesNum);
    return applyBrowseDisplaySort(filtered, sortMode);
  }, [results, minVotesNum, sortMode]);

  const handlePrev = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages) setPage(page + 1);
  };

  return (
    <>
      <section className="container">
        <div className="back">
          <Link className="btn" to="/">
            Back To Home
          </Link>
        </div>

        <h2>
          {totalResults > 0
            ? `${totalResults} Results for "${searchTerm}"`
            : searchTerm
              ? `No results found for "${searchTerm}"`
              : "Please enter a search term"}
        </h2>

        {searchTerm.trim() && results.length > 0 && (
          <div className="search-browse-toolbar">
            <label className="discover-field">
              <span className="discover-label">Sort</span>
              <select
                value={sortMode}
                onChange={(e) =>
                  setSortMode(e.target.value as BrowseResultSortMode)
                }
              >
                {BROWSE_SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="discover-field">
              <span className="discover-label">Min. votes</span>
              <select
                value={minVotes}
                onChange={(e) => setMinVotes(e.target.value)}
              >
                {MIN_VOTES_OPTIONS.map((o) => (
                  <option key={o.value || "none"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        {searchTerm.trim() && results.length > 0 && (
          <p className="search-browse-hint text-muted">
            Sort and filters apply to this page of results only.
          </p>
        )}

        <div id="search-results" className="grid">
          {displayResults.map((item) => {
            const title = searchType === "tv" ? item.name : item.title;
            const dateLabel = searchType === "tv" ? "Air Date" : "Release";
            const date =
              searchType === "tv" ? item.first_air_date : item.release_date;
            const detailPath =
              searchType === "tv"
                ? `/tv-details/${item.id}`
                : `/movie-details/${item.id}`;

            return (
              <div className="card" key={item.id}>
                <Link to={detailPath}>
                  <img
                    src={
                      item.poster_path
                        ? `https://image.tmdb.org/t/p/w500${item.poster_path}`
                        : `/images/no-image.jpg`
                    }
                    className="card-img-top"
                    alt={title || "Poster"}
                  />
                </Link>
                <div className="card-body">
                  <h5 className="card-title">{title}</h5>
                  <p className="card-text">
                    <small className="text-muted">
                      {dateLabel}: {date || "N/A"}
                    </small>
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {searchTerm.trim() &&
          results.length > 0 &&
          displayResults.length === 0 && (
            <p className="text-muted" style={{ marginTop: "1rem" }}>
              No titles on this page match the minimum vote filter.
            </p>
          )}

        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "20px",
              margin: "30px 0",
            }}
          >
            <button
              className="btn"
              type="button"
              onClick={handlePrev}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              className="btn"
              type="button"
              onClick={handleNext}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </>
  );
};

export default SearchPage;
